// ============================================================
// src/pages/promo.js
//
// -- WHAT THIS PAGE DOES -------------------------------------
// Lets a performer pick a month and generate a printable
// Instagram-square (1080x1080px) gig poster with:
//   - Their performer name (from auth user_metadata)
//   - Month + year header
//   - All gigs for that month in two columns (8 per column)
//   - Each gig row: date / show name / venue | city, state | time | age | cover
//   - Customisable background color or photo, overlay opacity,
//     accent / date / text colors, header font, body font
//   - More than 16 gigs auto-rolls onto "April 2026 (Part 2)" etc.
//   - Download as PNG (html2canvas @ 3x resolution)
//   - Share via Web Share API (falls back gracefully)
//
// -- SUPABASE SQL REQUIRED -----------------------------------
// Your existing gigs table needs four new columns.
// Run this ONCE in your Supabase SQL editor:
//
//   ALTER TABLE gigs
//     ADD COLUMN IF NOT EXISTS city             text DEFAULT '',
//     ADD COLUMN IF NOT EXISTS state            text DEFAULT '',
//     ADD COLUMN IF NOT EXISTS age_restriction  text DEFAULT '',
//     ADD COLUMN IF NOT EXISTS cover_charge     text DEFAULT '';
//
// -- NPM DEPENDENCY ------------------------------------------
//   npm install html2canvas
// ============================================================


// ============================================================
// STEP 1 -- IMPORTS
// ============================================================

import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useGigs }    from '../hooks/useGigs'
import { supabase }   from '../lib/supabaseClient'
import { DateInput }  from '../components/FormInputs'
import './links.css'

// BlueskySVG -- inline SVG for the Bluesky butterfly logo.
// Used instead of a font icon so html2canvas captures it correctly
// on the poster, and so no extra icon library version is required.
// color is passed as a prop so it matches the poster accent color.
function BlueskySVG({ color = 'currentColor', size = '1em' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
      width={size}
      height={size}
      fill={color}
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M439.8 358.7C436.5 358.3 433.1 357.9 429.8 357.4C433.2 357.8 436.5 358.3 439.8 358.7zM320 291.1C293.9 240.4 222.9 145.9 156.9 99.3C93.6 54.6 69.5 62.3 53.6 69.5C35.3 77.8 32 105.9 32 122.4C32 138.9 41.1 258 47 277.9C66.5 343.6 136.1 365.8 200.2 358.6C203.5 358.1 206.8 357.7 210.2 357.2C206.9 357.7 203.6 358.2 200.2 358.6C106.3 372.6 22.9 406.8 132.3 528.5C252.6 653.1 297.1 501.8 320 425.1C342.9 501.8 369.2 647.6 505.6 528.5C608 425.1 533.7 372.5 439.8 358.6C436.5 358.2 433.1 357.8 429.8 357.3C433.2 357.7 436.5 358.2 439.8 358.6C503.9 365.7 573.4 343.5 593 277.9C598.9 258 608 139 608 122.4C608 105.8 604.7 77.7 586.4 69.5C570.6 62.4 546.4 54.6 483.2 99.3C417.1 145.9 346.1 240.4 320 291.1z"/>
    </svg>
  )
}

// PatreonSVG -- inline SVG for the Patreon logo.
// Same approach as BlueskySVG -- avoids icon font dependency.
function PatreonSVG({ color = 'currentColor', size = '1em' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
      width={size}
      height={size}
      fill={color}
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M554 217.8C553.9 152.4 503 98.8 443.3 79.5C369.1 55.5 271.3 59 200.4 92.4C114.6 132.9 87.6 221.7 86.6 310.2C85.8 383 93 574.6 201.2 576C281.5 577 293.5 473.5 330.7 423.7C357.1 388.2 391.2 378.2 433.1 367.8C505.1 350 554.2 293.1 554.1 217.8L554 217.8z"/>
    </svg>
  )
}


// ============================================================
// STEP 2 -- CONSTANTS
// ============================================================

// Maximum gigs per poster before a new poster is created.
const GIGS_PER_POSTER = 10

// Header font options -- every entry in this list is either a
// naturally all-caps font or a font that renders well in all-caps.
// The performer name is forced to uppercase on the poster regardless,
// so all-caps fonts make the most impact here.
//
// Add these to public/index.html Google Fonts <link>:
//   family=Fredoka+One
//   family=Pacifico             (replaces Rochester -- bolder, far more readable)
//   family=New+Rocker
//   family=Bebas+Neue
//   family=Teko:wght@700
//   family=Anton
const HEADER_FONTS = [
  { label: 'Fredoka One', value: 'Fredoka One' }, // bubbly rounded, great in caps
  { label: 'Pacifico',    value: 'Pacifico'    }, // bold readable cursive -- replaces Rochester
  { label: 'New Rocker',  value: 'New Rocker'  }, // metal / gothic
  { label: 'Bebas Neue',  value: 'Bebas Neue'  }, // tall condensed, classic poster
  { label: 'Teko',        value: 'Teko'        }, // clean modern all-caps
  { label: 'Anton',       value: 'Anton'       }, // bold impact-style all-caps
]

const BODY_FONTS = [
  { label: 'Atkinson',  value: 'Atkinson Hyperlegible' },
  { label: 'Open Sans', value: 'Open Sans'             },
  { label: 'Lato',      value: 'Lato'                  },
  { label: 'DM Sans',   value: 'DM Sans'               },
]


// ============================================================
// STEP 3 -- HELPERS
// ============================================================

// parseDate -- timezone-safe date parse.
// Passing three numbers to new Date() uses local time, which
// avoids the UTC midnight offset bug that shifts dates back
// one day in negative-offset timezones.
function parseDate(dateStr) {
  const str          = String(dateStr).split('T')[0]
  const [yr, mo, dy] = str.split('-').map(Number)
  return new Date(yr, mo - 1, dy)
}

// fmtDate -- short human-readable date label for the poster.
// e.g. "Thu, Apr 2"
function fmtDate(dateStr) {
  return parseDate(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month:   'short',
    day:     'numeric',
  })
}

// fmtTime -- converts a stored time string into a readable 12-hour label.
// Supabase stores time as "HH:MM:SS" (24-hour with seconds).
// Also handles "HH:MM" and "h:mm AM/PM" formats in case they sneak in.
// Returns '' if no time provided so the segment is simply omitted.
// e.g. "20:00:00" => "8:00 PM"    "14:30:00" => "2:30 PM"
function fmtTime(timeStr) {
  if (!timeStr) return ''
  const t = timeStr.trim().toLowerCase()

  // HH:MM:SS (Supabase time column format)
  const withSeconds = t.match(/^(\d{1,2}):(\d{2}):\d{2}$/)
  if (withSeconds) {
    let h = parseInt(withSeconds[1], 10)
    const m = withSeconds[2]
    const ampm = h >= 12 ? 'PM' : 'AM'
    if (h === 0) h = 12
    else if (h > 12) h -= 12
    return `${h}:${m} ${ampm}`
  }

  // HH:MM (no seconds)
  const noSeconds = t.match(/^(\d{1,2}):(\d{2})$/)
  if (noSeconds) {
    let h = parseInt(noSeconds[1], 10)
    const m = noSeconds[2]
    const ampm = h >= 12 ? 'PM' : 'AM'
    if (h === 0) h = 12
    else if (h > 12) h -= 12
    return `${h}:${m} ${ampm}`
  }

  // Already 12-hour (pass-through): "8:00 PM", "10:30am"
  const twelveHour = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/)
  if (twelveHour) {
    const ampm = twelveHour[3].toUpperCase()
    return `${parseInt(twelveHour[1], 10)}:${twelveHour[2]} ${ampm}`
  }

  // Unrecognised format -- return as-is so something shows up
  return timeStr
}

// chunk -- splits an array into sub-arrays of length n.
// Used to break filteredGigs into GIGS_PER_POSTER-sized groups.
function chunk(arr, n) {
  const result = []
  for (let i = 0; i < arr.length; i += n) {
    result.push(arr.slice(i, i + n))
  }
  return result
}


// ============================================================
// STEP 4 -- COMPONENT
// ============================================================

export default function PromoGenerator() {

  // -- Gig data from Supabase via the shared hook
  const { gigs, loading } = useGigs()
  // gigs = all gigs for this user, filtered and grouped into poster pages below

  // -- Performer name read from auth user_metadata.
  // Used as the big headline at the top of each poster.
  const [performerName, setPerformerName] = useState('Your Name')

  // -- Social handles read from auth user_metadata.
  // Used in the poster footer to show the performer's platforms.
  const [socialHandles, setSocialHandles] = useState({
    instagram: '',
    twitter_x: '',
    tiktok:    '',
    bluesky:   '',
    youtube:   '',
    patreon:   '',
  })

  // ── GOOGLE FONTS INJECTION ──────────────────────────────────
  // Inject the Google Fonts <link> directly from the component so
  // all header and body fonts load reliably without requiring a
  // manual edit to public/index.html. The effect checks for an
  // existing link first so it never double-injects on re-renders.
  useEffect(() => {
    const FONT_ID   = 'dpp-promo-google-fonts'
    const FONTS_URL =
      'https://fonts.googleapis.com/css2?' +
      'family=Fredoka+One&' +
      'family=Pacifico&' +
      'family=New+Rocker&' +
      'family=Bebas+Neue&' +
      'family=Teko:wght@700&' +
      'family=Anton&' +
      'family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&' +
      'family=Open+Sans:wght@400;700&' +
      'family=Lato:wght@400;700&' +
      'family=DM+Sans:wght@400;700&' +
      'display=swap'

    if (!document.getElementById(FONT_ID)) {
      // Preconnect for faster font delivery
      const pre1 = document.createElement('link')
      pre1.rel  = 'preconnect'
      pre1.href = 'https://fonts.googleapis.com'
      document.head.appendChild(pre1)

      const pre2 = document.createElement('link')
      pre2.rel         = 'preconnect'
      pre2.href        = 'https://fonts.gstatic.com'
      pre2.crossOrigin = 'anonymous'
      document.head.appendChild(pre2)

      const link = document.createElement('link')
      link.id   = FONT_ID
      link.rel  = 'stylesheet'
      link.href = FONTS_URL
      document.head.appendChild(link)
    }
  }, [])

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      const name = user.user_metadata?.performer_name
      if (name) setPerformerName(name)
      const m = user.user_metadata || {}
      setSocialHandles({
        instagram: m.instagram  || '',
        twitter_x: m.twitter_x  || '',
        tiktok:    m.tiktok     || '',
        bluesky:   m.bluesky    || '',
        youtube:   m.youtube    || '',
        patreon:   m.patreon    || '',
      })
    })
  }, [])


  // -- Date range selector state
  const now = new Date()
  const defaultStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const defaultEnd   = (() => {
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`
  })()
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate,   setEndDate]   = useState(defaultEnd)

  // quickRange -- sets start + end to a named preset.
  const quickRange = (preset) => {
    const t = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    if (preset === 'this-month') {
      const first = new Date(t.getFullYear(), t.getMonth(), 1)
      const last  = new Date(t.getFullYear(), t.getMonth() + 1, 0)
      setStartDate(fmt(first))
      setEndDate(fmt(last))
    } else if (preset === 'next-month') {
      const first = new Date(t.getFullYear(), t.getMonth() + 1, 1)
      const last  = new Date(t.getFullYear(), t.getMonth() + 2, 0)
      setStartDate(fmt(first))
      setEndDate(fmt(last))
    } else if (preset === '30-days') {
      const end30 = new Date(t)
      end30.setDate(end30.getDate() + 30)
      setStartDate(fmt(t))
      setEndDate(fmt(end30))
    }
  }


  // -- Poster config state -- colors + fonts
  const [bgColor,      setBgColor]      = useState('#ffffff')
  // background color of the poster canvas
  const [accentColor,  setAccentColor]  = useState('#000000')
  // used for date column text and decorative elements
  const [dateColor,    setDateColor]    = useState('#000000')
  // separate color for the date text column
  const [textColor,    setTextColor]    = useState('#000000')
  // main body text color for gig names, venues, times
  const [headerFont,   setHeaderFont]   = useState('Fredoka One')
  // font used for the performer name headline at the top
  const [bodyFont,     setBodyFont]     = useState('Atkinson Hyperlegible')
  // font used for the gig list rows

  // -- Column layout state
  // Controls how gigs are split between the two poster columns.
  //   'balanced'   -- even split: Math.ceil(n/2) left, rest right
  //                   (9 gigs = 5 left, 4 right)
  //   'fill-left'  -- pack left column first up to 6, overflow to right
  //                   (9 gigs = 6 left, 3 right) -- like Word "Left" layout
  //   'fill-right' -- pack right column first, leave left sparse
  //                   (9 gigs = 3 left, 6 right) -- like Word "Right" layout
  const [columnLayout, setColumnLayout] = useState('balanced')

  // -- Background photo state
  const [bgImageUrl,     setBgImageUrl]     = useState(null)
  // null = no background photo, string = object URL or remote URL
  const [bgImageName,    setBgImageName]    = useState('')
  // filename of the selected background photo (for display only)
  const [overlayOpacity, setOverlayOpacity] = useState(0.55)
  // 0 = fully transparent overlay (photo fully visible)
  // 1 = fully opaque overlay (photo completely hidden)
  // 0.55 = default -- readable text with visible photo behind it
  const [bgPosition,     setBgPosition]     = useState({ x: 50, y: 50 })
  // percentage position of the photo within the canvas
  // { x: 50, y: 50 } = centered
  const dragState = useRef(null)
  // tracks drag state when the user repositions the background photo

  // -- Export state
  const [exporting,    setExporting]    = useState(false)
  // true while html2canvas is rendering the poster to PNG
  const [exportMsg,    setExportMsg]    = useState('')
  // status message shown below the download button

  // previewSize -- fits the available space to the right of the controls panel.
  // On desktop the controls panel is 380px + 1rem gap, so subtract that plus
  // some margin. On mobile it fills the full width minus padding.
  // The poster is always square 1080x1080 so width = height = previewSize.
  const [previewSize, setPreviewSize] = useState(() => {
    if (window.innerWidth < 768) return Math.min(500, window.innerWidth - 32)
    // desktop: total width minus sidebar (250px) minus controls (380px) minus gaps/padding
    return Math.max(300, Math.min(500, window.innerWidth - 250 - 380 - 80))
  })
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setPreviewSize(Math.min(500, window.innerWidth - 32))
      } else {
        setPreviewSize(Math.max(300, Math.min(500, window.innerWidth - 250 - 380 - 80)))
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // posterRefs -- one ref per rendered poster div for html2canvas.
  const posterRefs = useRef([])


  // ============================================================
  // STEP 5 -- DERIVED DATA
  // ============================================================

  // filteredGigs -- gigs within the selected date range, sorted ascending.
  const filteredGigs = useMemo(() => {
    if (!startDate || !endDate) return []
    const start = parseDate(startDate)
    const end   = parseDate(endDate)
    return gigs
      .filter(g => {
        const d = parseDate(g.date)
        return d >= start && d <= end && !g.is_private
      })
      .sort((a, b) => {
        const dateDiff = parseDate(a.date) - parseDate(b.date)
        if (dateDiff !== 0) return dateDiff
        // same day -- fall back to raw time string so gigs on the
        // same date still print earliest-to-latest on the poster
        return (a.time || '').localeCompare(b.time || '')
      })
  }, [gigs, startDate, endDate])

  // posterChunks -- array of arrays, each inner array = one poster's gigs.
  const posterChunks = useMemo(() => chunk(filteredGigs, GIGS_PER_POSTER), [filteredGigs])


  // ============================================================
  // STEP 6 -- PHOTO HANDLERS
  // ============================================================

  const handlePhoto = useCallback((e) => {
    const file = e.target.files[0]
    if (!file) return
    if (bgImageUrl) URL.revokeObjectURL(bgImageUrl)
    const url = URL.createObjectURL(file)
    setBgImageUrl(url)
    setBgImageName(file.name)
    setBgPosition({ x: 50, y: 50 })
  }, [bgImageUrl])

  const clearPhoto = useCallback(() => {
    if (bgImageUrl) URL.revokeObjectURL(bgImageUrl)
    setBgImageUrl(null)
    setBgImageName('')
    setBgPosition({ x: 50, y: 50 })
  }, [bgImageUrl])

  useEffect(() => {
    return () => { if (bgImageUrl) URL.revokeObjectURL(bgImageUrl) }
  }, [bgImageUrl])


  // ============================================================
  // STEP 7 -- DOWNLOAD HANDLER
  // ============================================================

  const handleDownload = useCallback(async () => {
    if (posterRefs.current.length === 0) return

    setExporting(true)
    setExportMsg('Generating poster...')

    try {
      const html2canvas = (await import('html2canvas')).default
      await document.fonts.ready

      for (let i = 0; i < posterRefs.current.length; i++) {
        const node = posterRefs.current[i]
        if (!node) continue

        const canvas = await html2canvas(node, {
          scale:           3,
          useCORS:         true,
          allowTaint:      true,
          backgroundColor: null,
        })

        const blob = await new Promise(res => canvas.toBlob(res, 'image/png'))
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')

        // Filename format: PerformerName_YYYYMMDD-YYYYMMDD_Xgigs.png
        // e.g. KingPerk_20260322-20260415_8gigs.png
        // sanitize performer name: spaces → underscores, strip special chars
        const cleanName  = performerName.trim()
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '_')
          || 'Performer'
        const startSlug  = startDate.replace(/-/g, '')
        const endSlug    = endDate.replace(/-/g, '')
        const gigCount   = posterChunks[i].length
        const partSuffix = posterChunks.length > 1 ? `_part${i + 1}` : ''

        a.href     = url
        a.download = `${cleanName}_${startSlug}-${endSlug}_${gigCount}gigs${partSuffix}.png`
        a.click()
        URL.revokeObjectURL(url)

        if (i < posterRefs.current.length - 1) {
          await new Promise(res => setTimeout(res, 400))
        }
      }

      setExportMsg(
        posterChunks.length > 1
          ? `${posterChunks.length} posters downloaded`
          : 'Poster downloaded'
      )
    } catch (err) {
      console.error('PromoGenerator: html2canvas error', err)
      setExportMsg('Export failed -- check the console for details')
    } finally {
      setExporting(false)
      setTimeout(() => setExportMsg(''), 4000)
    }
  }, [posterChunks, startDate, endDate, performerName])


  // ============================================================
  // STEP 8 -- POSTER BUILDER HELPERS
  // ============================================================

  // buildPosterStyle -- inline style for the 1080x1080 poster container.
  // html2canvas reads inline styles so CSS custom properties don't work
  // inside the poster -- everything must be hard-coded values.
  function buildPosterStyle() {
    return {
      width:               '1080px',
      height:              '1080px',
      position:            'relative',
      overflow:            'hidden',
      boxSizing:           'border-box',
      fontFamily:          `'${bodyFont}', sans-serif`,
      backgroundColor:     bgImageUrl ? 'transparent' : bgColor,
      backgroundImage:     bgImageUrl ? `url(${bgImageUrl})` : 'none',
      backgroundSize:      'cover',
      backgroundPosition:  `${bgPosition.x}% ${bgPosition.y}%`,
    }
  }

  // ── FONT SIZE TIERS ──────────────────────────────────────────
  // Font sizes are based on tallCol -- the number of rows in the
  // TALLER of the two columns -- not the total gig count.
  //
  // Why tallCol and not count?
  //   Balanced 8 gigs  → 4 rows per column   → tallCol = 4 (roomy)
  //   Left-heavy 8 gigs → 6L / 2R            → tallCol = 6 (tight)
  //   Balanced 12 gigs  → 6 rows per column  → tallCol = 6 (tight)
  //
  // This means fonts automatically get bigger when you switch from
  // left-heavy to balanced with the same gig count, because balanced
  // spreads rows more evenly so each column is shorter and has more
  // space per row. Left/right heavy fills one column all the way to
  // 6 so fonts shrink to fit.
  //
  // Tiers:
  //   tallCol ≤ 2: very roomy  (1-4 total gigs, balanced)
  //   tallCol ≤ 3: comfortable (5-6 total gigs, balanced)
  //   tallCol ≤ 4: moderate    (7-8 total gigs, balanced / 8-9 heavy)
  //   tallCol ≤ 5: compact     (9-10 total gigs, balanced)
  //   tallCol ≤ 6: dense       (11-12 balanced / any layout hitting 6 per col)

  function gigFS(tallCol) {
    if (tallCol <= 2) return '48px'
    if (tallCol <= 3) return '40px'
    if (tallCol <= 4) return '32px'
    if (tallCol <= 5) return '26px'
    return '21px'
  }
  function showFS(tallCol) {
    if (tallCol <= 2) return '88px'
    if (tallCol <= 3) return '68px'
    if (tallCol <= 4) return '52px'
    if (tallCol <= 5) return '40px'
    return '31px'
  }

  // nameFS -- showFS scaled down for long show names.
  //   ≤15 chars → full size
  //   16-25     → 85%
  //   26-35     → 70%
  //   36+       → 58%
  function nameFS(tallCol, name) {
    const base = parseInt(showFS(tallCol), 10)
    const len  = (name || '').length
    let scale  = 1
    if (len > 35)      scale = 0.58
    else if (len > 25) scale = 0.70
    else if (len > 15) scale = 0.85
    return `${Math.round(base * scale)}px`
  }

  function venueFS(tallCol) {
    // Intentionally small -- venue is supporting detail, not the headline.
    if (tallCol <= 2) return '26px'
    if (tallCol <= 3) return '22px'
    if (tallCol <= 4) return '18px'
    if (tallCol <= 5) return '15px'
    return '13px'
  }

  // rowPad -- tiny buffer around the divider line.
  // space-between handles all real vertical distribution.
  function rowPad() {
    return '3px 0'
  }

  // ============================================================
  // buildSegments -- constructs the venue detail line for one gig.
  //
  // Order: venue | city, state | show time | age restriction | cover charge
  //
  // Each segment is only added if it has a value, so there are no
  // trailing " | " separators for fields the performer left blank.
  // fmtTime() converts "20:00:00" → "8:00 PM" for the time segment.
  // ============================================================
  function buildSegments(gig) {
    const segs = []

    // Segment 1: venue name
    if (gig.venue_name)        segs.push(gig.venue_name)

    // Segment 2: city, state (combined) or just city if no state
    if (gig.city && gig.state) segs.push(`${gig.city}, ${gig.state}`)
    else if (gig.city)         segs.push(gig.city)

    // Segment 3: show time -- formatted from 24-hour Supabase format
    const timeLabel = fmtTime(gig.time)
    if (timeLabel)             segs.push(timeLabel)

    // Segment 4: age restriction (e.g. "21+", "18+", "All Ages")
    if (gig.age_restriction)   segs.push(gig.age_restriction)

    // Segment 5: cover charge (e.g. "Free", "$10", "$5-$15")
    if (gig.cover_charge)      segs.push(gig.cover_charge)

    return segs
  }


  // ============================================================
  // STEP 9 -- SOCIAL FOOTER RENDERER
  // Shared between the hidden capture div and the visible preview.
  // Extracted to a function so we don't duplicate the JSX twice.
  // ============================================================
  function renderSocialFooter(bFont) {
    const platforms = [
      { key: 'instagram', icon: 'bi-instagram',  prefix: '@' },
      { key: 'twitter_x', icon: 'bi-twitter-x',  prefix: '@' },
      { key: 'tiktok',    icon: 'bi-tiktok',     prefix: '@' },
      { key: 'bluesky',   icon: null, svg: (color) => <BlueskySVG color={color} size="20px" />, prefix: '@' },
      { key: 'youtube',   icon: 'bi-youtube',    prefix: '@' },
      { key: 'patreon',   icon: null, svg: (color) => <PatreonSVG color={color} size="20px" />, prefix: '@' },
    ]
    const active = platforms.filter(p => socialHandles[p.key])

    return (
      <div style={{
        borderTop:      `1px solid ${accentColor}44`,
        paddingTop:     '10px',
        marginTop:      'auto',
        // flexShrink: 0 prevents the footer from being squashed or pushed
        // outside the poster when a gig column is very tall (e.g. left-heavy
        // with 8 gigs). The poster content div is a flex column so this
        // guarantees the footer always takes its full height at the bottom.
        flexShrink:     0,
        display:        'flex',
        justifyContent: active.length > 0 ? 'center' : 'space-between',
        alignItems:     'center',
        gap:            '20px',
        flexWrap:       'wrap',
      }}>
        {active.length > 0 ? (
          active.map(p => (
            <span
              key={p.key}
              style={{
                fontFamily:  bFont,
                fontSize:    '15px',
                color:       textColor,
                opacity:     0.85,
                display:     'flex',
                alignItems:  'center',
                gap:         '6px',
                letterSpacing: '.01em',
              }}
            >
              {p.svg
                ? p.svg(accentColor)
                : <i className={`bi ${p.icon}`} style={{ fontSize: '16px', color: accentColor }}></i>
              }
              {p.prefix}{socialHandles[p.key]}
            </span>
          ))
        ) : (
          <span style={{ fontFamily: bFont, fontSize: '13px', color: textColor, opacity: .35 }}>
            Drag Pocket Planner
          </span>
        )}
      </div>
    )
  }


  // ── activeColorPicker ────────────────────────────────────────
  // Tracks which color picker popover is open: 'accent', 'dates',
  // 'names', 'bg', or null (all closed).
  const [activeColorPicker, setActiveColorPicker] = useState(null)
  const colorPickerRef = useRef(null)

  // Close the active picker when clicking outside it
  useEffect(() => {
    const handler = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setActiveColorPicker(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ── ColorPicker ──────────────────────────────────────────────
  // A full gradient color picker popover with:
  //   - 2D saturation/brightness canvas (click or drag to pick)
  //   - Hue slider below the canvas
  //   - Hex input field for direct entry
  // Works on desktop and mobile (pointer events handle both).
  //
  // Internals use HSV (hue, saturation, value) because that maps
  // directly to the 2D canvas gradient. Converts to/from hex for
  // the external color state.

  // hexToHsv / hsvToHex -- color conversion helpers used by the picker.
  function hexToHsv(hex) {
    // expand shorthand #rgb to #rrggbb
    const full = hex.replace(/^#([a-f\d])([a-f\d])([a-f\d])$/i,
      (_, r, g, b) => '#' + r + r + g + g + b + b)
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full)
    if (!result) return { h: 0, s: 1, v: 1 }
    let r = parseInt(result[1], 16) / 255
    let g = parseInt(result[2], 16) / 255
    let b = parseInt(result[3], 16) / 255
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    const d = max - min
    let h = 0, s = max === 0 ? 0 : d / max, v = max
    if (max !== min) {
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
      else if (max === g) h = ((b - r) / d + 2) / 6
      else h = ((r - g) / d + 4) / 6
    }
    return { h: h * 360, s, v }
  }

  function hsvToHex(h, s, v) {
    const f = (n) => {
      const k = (n + h / 60) % 6
      return v - v * s * Math.max(0, Math.min(k, 4 - k, 1))
    }
    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0')
    return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`
  }

  function ColorPicker({ label, value, setter, pickerId }) {
    const isOpen        = activeColorPicker === pickerId
    const canvasRef     = useRef(null)
    const hsv           = hexToHsv(value)
    const [hue, setHue] = useState(hsv.h)
    // localHex mirrors the hex input so the user can type freely
    const [localHex, setLocalHex] = useState(value)

    // Keep localHex in sync when value changes from outside
    useEffect(() => { setLocalHex(value) }, [value])

    // Redraw the SV canvas whenever hue changes
    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx    = canvas.getContext('2d')
      const W      = canvas.width
      const H      = canvas.height
      // White → hue gradient (left to right = saturation)
      const gradH  = ctx.createLinearGradient(0, 0, W, 0)
      gradH.addColorStop(0, '#fff')
      gradH.addColorStop(1, `hsl(${hue},100%,50%)`)
      ctx.fillStyle = gradH
      ctx.fillRect(0, 0, W, H)
      // Transparent → black gradient (top to bottom = value)
      const gradV  = ctx.createLinearGradient(0, 0, 0, H)
      gradV.addColorStop(0, 'rgba(0,0,0,0)')
      gradV.addColorStop(1, 'rgba(0,0,0,1)')
      ctx.fillStyle = gradV
      ctx.fillRect(0, 0, W, H)
    }, [hue, isOpen])

    // pickFromCanvas -- converts a canvas coordinate to HSV and updates color
    const pickFromCanvas = useCallback((e) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const cx   = e.clientX ?? e.touches?.[0]?.clientX
      const cy   = e.clientY ?? e.touches?.[0]?.clientY
      const x    = Math.max(0, Math.min(1, (cx - rect.left)  / rect.width))
      const y    = Math.max(0, Math.min(1, (cy - rect.top)   / rect.height))
      const s    = x
      const v    = 1 - y
      const hex  = hsvToHex(hue, s, v)
      setter(hex)
      setLocalHex(hex)
    }, [hue, setter])

    // Drag on canvas
    const onCanvasPointerDown = (e) => {
      e.preventDefault()
      pickFromCanvas(e)
      const move = (me) => pickFromCanvas(me)
      const up   = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    }

    // Hue slider change
    const onHueChange = (e) => {
      const h   = parseFloat(e.target.value)
      setHue(h)
      const cur = hexToHsv(value)
      const hex = hsvToHex(h, cur.s, cur.v)
      setter(hex)
      setLocalHex(hex)
    }

    // Hex input change
    const onHexChange = (e) => {
      const v = e.target.value
      setLocalHex(v)
      if (/^#[0-9a-fA-F]{6}$/.test(v)) {
        setter(v)
        setHue(hexToHsv(v).h)
      }
    }

    // Current SV cursor position (for the draggable dot)
    const curHsv = hexToHsv(value)
    const dotX   = curHsv.s * 100
    const dotY   = (1 - curHsv.v) * 100

    return (
      <div className="lcp-wrapper" ref={isOpen ? colorPickerRef : null}>

        {/* Trigger row -- color dot + label + hex + chevron */}
        <button
          type="button"
          className="lcp-trigger"
          onClick={() => setActiveColorPicker(isOpen ? null : pickerId)}
        >
          <span className="lcp-label">{label}</span>
          <div className="lcp-trigger-right">
            {/* backgroundColor is a dynamic color value -- unavoidable inline style */}
            <div className="lcp-swatch" style={{ backgroundColor: value }} />
            <span className="lcp-hex-text">{value}</span>
            <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} lcp-chevron`} />
          </div>
        </button>

        {/* Popover */}
        {isOpen && (
          <div className="lcp-popover" ref={colorPickerRef}>

            {/* 2D SV gradient canvas */}
            <div className="lcp-canvas-wrap" onPointerDown={onCanvasPointerDown}>
              <canvas ref={canvasRef} width={170} height={110} className="lcp-canvas" />
              {/* Draggable dot -- left + top + backgroundColor are computed values: unavoidable inline styles */}
              <div
                className="lcp-dot"
                style={{ left: `${dotX}%`, top: `${dotY}%`, backgroundColor: value }}
              />
            </div>

            {/* Hue slider */}
            <input
              type="range"
              min="0" max="360" step="1"
              value={hue}
              onChange={onHueChange}
              className="lcp-hue"
            />

            {/* Hex input + preview swatch */}
            <div className="lcp-hex-row">
              {/* backgroundColor is a dynamic color value -- unavoidable inline style */}
              <div className="lcp-hex-swatch" style={{ backgroundColor: value }} />
              <input
                type="text"
                value={localHex}
                maxLength={7}
                onChange={onHexChange}
                className="lcp-hex-input"
                placeholder="#000000"
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container py-4" style={{ maxWidth: '1200px' }}>
        <h1 className="page-title">
          Promo Generator
        </h1>
        <p>Loading your gigs...</p>
      </div>
    )
  }


  // ============================================================
  // STEP 11 -- MAIN RENDER
  // ============================================================

  return (
    <div className="container py-3" style={{ maxWidth: '1200px' }}>

      {/* ── A) PAGE HEADER ── */}
      <h1 className="page-title mb-1">
        Promo Generator
      </h1>
      <p className="text-muted small mb-3">
        Pick a date range, customise your poster, then download or share.
      </p>


      {/* ════════════════════════════════════════════════════════
          RESPONSIVE WRAPPER
          Mobile  (< 768px): single column, controls on top, preview below.
          Desktop (>= 768px): side-by-side, controls left, preview right.
      ════════════════════════════════════════════════════════ */}
      <div style={{
        display:       'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap:           '1rem',
        alignItems:    'flex-start',
      }}>

        {/* ════════════════════════════════════════════════════
            B) CONTROLS PANEL
        ════════════════════════════════════════════════════ */}
        <div style={{
          width:     isMobile ? '100%' : '380px',
          flexShrink: 0,
        }}>


          {/* ── B1) DATE RANGE ── */}
          <div className="card mb-3">
            <div className="card-header">
              <strong>
                <i className="bi bi-calendar-range me-2"></i>Date Range
              </strong>
            </div>
            <div className="card-body p-3">

              {/* Quick-select preset buttons */}
              <div className="d-flex gap-2 mb-3">
                {[
                  { label: 'This Month', preset: 'this-month' },
                  { label: 'Next Month', preset: 'next-month' },
                  { label: '30 Days',    preset: '30-days'    },
                ].map(({ label, preset }) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => quickRange(preset)}
                    className="le-preset-btn"
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* From / To date inputs on one row */}
           {/* From / To date inputs — stack vertically on mobile */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <DateInput
                  label="From"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <DateInput
                  label="To"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              {/* Gig count summary */}
              {startDate && endDate && (
                filteredGigs.length === 0 ? (
                  <p className="small text-muted mt-2 mb-0">No gigs in this range.</p>
                ) : (
                  <p className="small mt-2 mb-0 fw-bold" style={{ color: 'var(--secondary-dark)' }}>
                    <i className="bi bi-check-circle me-1"></i>
                    {filteredGigs.length} gig{filteredGigs.length !== 1 ? 's' : ''} --{' '}
                    {posterChunks.length} poster{posterChunks.length !== 1 ? 's' : ''}
                  </p>
                )
              )}

            </div>
          </div>


          {/* ── B2) BACKGROUND + COLORS ── */}
          <div className="card mb-3" style={{ overflow: 'visible' }}>
            <div className="card-header">
              <strong>
                <i className="bi bi-palette me-2"></i>Background &amp; Colors
              </strong>
            </div>
            <div className="card-body" style={{ overflow: 'visible' }}>
              <div className="le-split">

                {/* Left: Background */}
                <div className="le-split-col">
                  <p className="le-split-col-label">Background</p>

                  <input
                    type="file"
                    id="bgPhotoInput"
                    accept="image/*"
                    className="d-none"
                    onChange={handlePhoto}
                  />
                  <label
                    htmlFor="bgPhotoInput"
                    className="btn btn-outline-secondary btn-sm w-100 mb-2"
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <i className="bi bi-upload me-1"></i>
                    {bgImageName ? 'Change' : 'Photo'}
                  </label>

                  {bgImageUrl ? (
                    <>
                      <button
                        className="btn btn-sm btn-outline-danger w-100 mb-2"
                        onClick={clearPhoto}
                      >
                        <i className="bi bi-x-circle me-1"></i>Remove
                      </button>
                      <label className="form-label small fw-semibold mb-1">Overlay</label>
                      <input
                        type="range"
                        className="form-range"
                        min="0" max="0.9" step="0.05"
                        value={overlayOpacity}
                        onChange={e => setOverlayOpacity(parseFloat(e.target.value))}
                      />
                      <div className="d-flex justify-content-between" style={{ fontSize: '.7rem', opacity: .5 }}>
                        <span>Light</span><span>Dark</span>
                      </div>
                    </>
                  ) : (
                    <div className="le-bg-row">
                      <input
                        type="color"
                        className="le-bg-input"
                        value={bgColor}
                        onChange={e => setBgColor(e.target.value)}
                      />
                      <span className="le-bg-hex">{bgColor}</span>
                    </div>
                  )}
                </div>

                <div className="le-divider-v" />

                {/* Right: Colors */}
                <div className="le-split-col">
                  <p className="le-split-col-label">Colors</p>
                  {/* ColorSwatch renders a dot + hex + popover with swatches.
                      "Names & Text" controls both the performer name headline
                      and all gig show names so they always match. */}
                  <ColorPicker label="Accent"       value={accentColor} setter={setAccentColor} pickerId="accent" />
                  <ColorPicker label="Dates"        value={dateColor}   setter={setDateColor}   pickerId="dates"  />
                  <ColorPicker label="Titles" value={textColor}   setter={setTextColor}   pickerId="names"  />
                </div>

              </div>
            </div>
          </div>


          {/* ── B3) FONTS ── */}
          <div className="card mb-3">
            <div className="card-header">
              <strong>
                <i className="bi bi-fonts me-2"></i>Fonts
              </strong>
            </div>
            <div className="card-body">
              <div className="le-split">

                {/* Heading font column */}
                <div className="le-split-col">
                  <p className="le-split-col-label">Heading</p>
                  {HEADER_FONTS.map(f => (
                    <button
                      key={f.value}
                      type="button"
                      className={`le-font-btn${headerFont === f.value ? ' le-font-btn--active' : ''}`}
                      // fontFamily preview -- unavoidable inline style
                      style={{ fontFamily: `'${f.value}', cursive, sans-serif` }}
                      onClick={() => setHeaderFont(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="le-divider-v" />

                {/* Body font column */}
                <div className="le-split-col">
                  <p className="le-split-col-label">Body</p>
                  {BODY_FONTS.map(f => (
                    <button
                      key={f.value}
                      type="button"
                      className={`le-font-btn${bodyFont === f.value ? ' le-font-btn--active' : ''}`}
                      // fontFamily preview -- unavoidable inline style
                      style={{ fontFamily: `'${f.value}', sans-serif` }}
                      onClick={() => setBodyFont(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

              </div>
            </div>
          </div>


          {/* ── B4) COLUMN LAYOUT ── */}
          {/* Controls how gigs are split across the two columns.
              Mirrors the Left / Balanced / Right column options in Word. */}
          <div className="card mb-3">
            <div className="card-header">
              <strong>
                <i className="bi bi-layout-three-columns me-2"></i>Column Layout
              </strong>
            </div>
            <div className="card-body p-3">

              <p className="small text-muted mb-2">
                How should gigs be split between the two columns?
              </p>

              {/* Three-way toggle matching Word's Left / Balanced / Right */}
              <div className="d-flex gap-2 flex-wrap">
                {[
                  {
                    key:   'fill-left',
                    label: 'Left Heavy',
                    icon:  'bi-text-indent-left',
                  },
                  {
                    key:   'balanced',
                    label: 'Balanced',
                    icon:  'bi-justify',
                  },
                  {
                    key:   'fill-right',
                    label: 'Right Heavy',
                    icon:  'bi-text-indent-right',
                  },
                ].map(opt => {
                  const isActive = columnLayout === opt.key
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setColumnLayout(opt.key)}
                      style={{
                        flex:          1,
                        display:       'flex',
                        flexDirection: 'column',
                        alignItems:    'center',
                        gap:           '5px',
                        padding:       '8px 4px',
                        borderRadius:  '8px',
                        border:        isActive
                          ? '2px solid var(--primary)'
                          : '2px solid var(--secondary-light)',
                        background:    isActive ? 'var(--pink-pale)' : 'var(--brand-white)',
                        color:         isActive ? 'var(--primary-accessible)' : 'var(--neutral-1)',
                        cursor:        'pointer',
                        transition:    'all .15s',
                        fontFamily:    'var(--font-body)',
                      }}
                    >
                      {/* Bootstrap Icon */}
                      <i className={`bi ${opt.icon}`} style={{ fontSize: '1.4rem' }}></i>
                      <span style={{ fontSize: '.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {opt.label}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/* Contextual description of the active mode */}
              <p className="small text-muted mt-2 mb-0" style={{ fontSize: '.72rem' }}>
                {columnLayout === 'balanced'  && 'Gigs split as evenly as possible. Odd gig goes left.'}
                {columnLayout === 'fill-left' && 'Left column fills first (up to 6), remainder on right.'}
                {columnLayout === 'fill-right'&& 'Right column fills first (up to 6), remainder on left.'}
              </p>

            </div>
          </div>


        </div>{/* end controls panel */}


        {/* ════════════════════════════════════════════════════
            C) POSTER PREVIEWS PANEL
            position: sticky on desktop so the preview stays
            visible while scrolling through the controls panel.
            alignSelf: flex-start is required -- without it the
            div stretches to match the controls panel height and
            sticky has nothing to stick to.
        ════════════════════════════════════════════════════ */}
        <div style={{
          flex:      1,
          minWidth:  0,
          width:     '100%',
          // sticky only applies on desktop (isMobile = false)
          // on mobile the preview sits below the controls so
          // sticky would just pin it off-screen
          position:  isMobile ? 'static' : 'sticky',
          top:       isMobile ? 'auto'   : '1rem',
          alignSelf: 'flex-start',
          // max-height + overflow-y lets the preview panel scroll
          // independently if there are multiple posters taller than
          // the viewport -- without this it would overflow the page
          maxHeight: isMobile ? 'none' : 'calc(100vh - 2rem)',
          overflowY: isMobile ? 'visible' : 'auto',
        }}>

          {filteredGigs.length === 0 ? (

            <div
              className="text-center py-5"
              style={{
                background: '#fff',
                border: '2px dashed var(--secondary-light)',
                borderRadius: 'var(--radius-xl)',
              }}
            >
              <i className="bi bi-calendar-x" style={{ fontSize: '2.5rem', color: 'var(--primary-light)' }}></i>
              <h5 className="mt-3" style={{ fontFamily: 'var(--font-display)', color: 'var(--neutral-1)' }}>
                No gigs in this date range
              </h5>
              <p className="small text-muted">Add gigs on the Gigs page or adjust the date range.</p>
            </div>

          ) : (

            posterChunks.map((chunk, chunkIdx) => {

              // rangeLabel for the poster header
              const buildRangeLabel = () => {
                if (!startDate || !endDate) return ''
                const s = parseDate(startDate)
                const e = parseDate(endDate)
                const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
                const sameYear  = s.getFullYear() === e.getFullYear()
                const monthName = (d, withYear) => d.toLocaleDateString('en-US', {
                  month: 'long',
                  ...(withYear ? { year: 'numeric' } : {}),
                })
                if (sameMonth) return monthName(s, true)
                if (sameYear)  return `${monthName(s, false)} - ${monthName(e, true)}`
                return `${monthName(s, true)} - ${monthName(e, true)}`
              }

              const isMulti    = posterChunks.length > 1
              const monthLabel = buildRangeLabel() + (chunkIdx > 0 ? ` (Part ${chunkIdx + 1})` : '')

              // ── COLUMN SPLIT ──────────────────────────────────────────
              // Respects the columnLayout control in the controls panel.
              //
              //   'balanced'   -- Math.ceil(n/2) left, rest right
              //                   9 gigs → 5L / 4R    8 gigs → 4L / 4R
              //
              //   'fill-left'  -- left fills to max (6) first, rest right
              //                   9 gigs → 6L / 3R    4 gigs → 4L / 0R
              //
              //   'fill-right' -- right fills to max (6) first, rest left
              //                   9 gigs → 3L / 6R    4 gigs → 0L / 4R
              //                   (achieved by putting the remainder on left
              //                    and the heavier chunk on right)
              //
              // MAX_COL = the largest either column can hold before
              // the poster overflows. Tied to GIGS_PER_POSTER / 2 = 6.
              const MAX_COL = Math.ceil(GIGS_PER_POSTER / 2)   // 6

              let splitAt
              if (columnLayout === 'fill-left') {
                // Left gets up to MAX_COL; right gets whatever is left
                splitAt = Math.min(chunk.length, MAX_COL)
              } else if (columnLayout === 'fill-right') {
                // Right gets up to MAX_COL; left gets the shorter remainder
                splitAt = Math.max(0, chunk.length - MAX_COL)
              } else {
                // balanced (default) -- as close to 50/50 as possible
                splitAt = Math.ceil(chunk.length / 2)
              }

              const leftCol    = chunk.slice(0, splitAt)
              const rightCol   = chunk.slice(splitAt)

              // tallCol = rows in the taller of the two columns.
              // This is what actually determines how much vertical space
              // each row has -- fonts are sized from this, not total count.
              // e.g. balanced 8 gigs → tallCol 4 (roomy)
              //      left-heavy 8 gigs → tallCol 6 (tight, same as 12 balanced)
              const tallCol    = Math.max(leftCol.length, rightCol.length)
              const hFont      = `'${headerFont}', cursive, sans-serif`
              const bFont      = `'${bodyFont}', sans-serif`
              // Scale the 1080px poster down to fit the preview area
              const posterScale = previewSize / 1080

              return (
                <div key={chunkIdx} style={{ marginBottom: '2rem' }}>

                  {/* Poster label badge */}
                  {isMulti && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span
                        className="badge rounded-pill"
                        style={{ background: 'var(--pink-pale)', color: 'var(--primary-accessible)', border: '1px solid var(--primary-light)', fontSize: '.76rem', fontWeight: 800, letterSpacing: '.08em' }}
                      >
                        POSTER {chunkIdx + 1} OF {posterChunks.length}
                      </span>
                      <span className="small text-muted">{chunk.length} gigs</span>
                    </div>
                  )}

                  {/* ── HIDDEN full-size poster for html2canvas capture ── */}
                  {/* Off-screen so it's in the DOM but never visible.       */}
                  {/* The ref points here so html2canvas uses the true        */}
                  {/* 1080x1080 element, unaffected by the CSS scale below.  */}
                  <div
                    ref={el => (posterRefs.current[chunkIdx] = el)}
                    style={{
                      ...buildPosterStyle(),
                      position: 'fixed',
                      top:      '-9999px',
                      left:     '-9999px',
                      zIndex:   -1,
                    }}
                  >
                    {/* Dark overlay on top of the background photo */}
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: `rgba(0,0,0,${bgImageUrl ? overlayOpacity : 0})` }} />

                    {/* All poster content sits above the overlay */}
                    <div style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', padding: '44px 60px 36px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>

                      {/* Header */}
                      <div style={{ textAlign: 'center', borderBottom: `3px solid ${accentColor}`, paddingBottom: '20px', marginBottom: '24px' }}>
                        <p style={{ fontFamily: hFont, fontSize: '56px', color: textColor, textShadow: `0 0 40px ${textColor}44`, margin: 0, lineHeight: 1, letterSpacing: '.02em' }}>{performerName}</p>
                        <p style={{ fontFamily: bFont, fontSize: '30px', color: textColor, opacity: .85, margin: '8px 0 0' }}>{monthLabel}</p>
                      </div>

                      {/* Two-column gig list */}
                      {/* IMPORTANT: both column divs always render even when
                          empty. Without this, fill-left/fill-right have no
                          spacer and the filled column just drifts to the left
                          regardless of which side it belongs on. An empty div
                          with flex:1 holds the correct space.
                          minHeight:0 + overflow:hidden on this wrapper allows
                          it to shrink when content is tall -- without this the
                          gig list grows past the poster boundary and pushes the
                          social footer out of the 1080px frame. */}
                      <div style={{ display: 'flex', gap: '40px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        {[leftCol, rightCol].map((col, colIdx) => (
                          <div key={colIdx} style={{
                            flex:           1,
                            minWidth:       0,
                            // minWidth:0 is required on flex children to allow
                            // overflow:hidden to work -- without it the div
                            // grows to fit its nowrap content and clips nothing
                            overflow:       'hidden',
                            display:        'flex',
                            flexDirection:  'column',
                            // space-between based on THIS column's own row count,
                            // not the total gig count. This prevents remainder
                            // columns in fill-left/fill-right (e.g. 2 gigs on
                            // the right) from spreading those 2 rows across the
                            // full column height with a massive gap between them.
                            // Threshold of 3: 1-2 rows pack together (flex-start),
                            // 3+ rows spread evenly (space-between).
                            justifyContent: col.length >= 3 ? 'space-between' : 'flex-start',
                          }}>
                            {col.map(gig => {
                              // ── VENUE DETAIL LINE: venue | city, state | time | age | cover ──
                              const segs = buildSegments(gig)
                              return (
                                <div key={gig.id} style={{ padding: rowPad(), borderBottom: `1px solid ${accentColor}33`, display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontFamily: bFont, fontSize: gigFS(tallCol), color: dateColor, lineHeight: 1.25 }}>{fmtDate(gig.date)}</span>
                                  <span style={{ fontFamily: hFont, fontSize: nameFS(tallCol, gig.name), color: textColor, lineHeight: 1.2, marginTop: '2px', display: 'block' }}>{gig.name || ''}</span>
                                  {segs.length > 0 && <span style={{ fontFamily: bFont, fontSize: venueFS(tallCol), color: accentColor, opacity: 1, marginTop: '3px', lineHeight: 1.25, display: 'block' }}>{segs.join(' | ')}</span>}
                                </div>
                              )
                            })}
                          </div>
                        ))}
                      </div>

                      {/* Social footer */}
                      {renderSocialFooter(bFont)}

                    </div>
                  </div>
                  {/* end hidden capture div */}


                  {/* ── VISIBLE scaled preview ── */}
                  {/* Width and height = previewSize. The actual poster is */}
                  {/* 1080x1080 but CSS scale brings it down to preview size. */}
                  <div
                    style={{
                      width: `${previewSize}px`, height: `${previewSize}px`,
                      overflow: 'hidden',
                      borderRadius: '16px', boxShadow: 'var(--shadow-lg)',
                      border: '3px solid var(--secondary-light)',
                      cursor: bgImageUrl ? 'grab' : 'default',
                      userSelect: 'none',
                    }}
                    onMouseDown={bgImageUrl ? (e) => {
                      e.preventDefault()
                      dragState.current = { startX: e.clientX, startY: e.clientY, startPosX: bgPosition.x, startPosY: bgPosition.y }
                      const onMove = (me) => {
                        if (!dragState.current) return
                        const dx = (me.clientX - dragState.current.startX) / (previewSize / 100)
                        const dy = (me.clientY - dragState.current.startY) / (previewSize / 100)
                        setBgPosition({
                          x: Math.min(100, Math.max(0, dragState.current.startPosX - dx)),
                          y: Math.min(100, Math.max(0, dragState.current.startPosY - dy)),
                        })
                      }
                      const onUp = () => {
                        dragState.current = null
                        window.removeEventListener('mousemove', onMove)
                        window.removeEventListener('mouseup', onUp)
                      }
                      window.addEventListener('mousemove', onMove)
                      window.addEventListener('mouseup', onUp)
                    } : undefined}
                    onTouchStart={bgImageUrl ? (e) => {
                      const t = e.touches[0]
                      dragState.current = { startX: t.clientX, startY: t.clientY, startPosX: bgPosition.x, startPosY: bgPosition.y }
                      const onMove = (te) => {
                        if (!dragState.current) return
                        const tt = te.touches[0]
                        const dx = (tt.clientX - dragState.current.startX) / (previewSize / 100)
                        const dy = (tt.clientY - dragState.current.startY) / (previewSize / 100)
                        setBgPosition({
                          x: Math.min(100, Math.max(0, dragState.current.startPosX - dx)),
                          y: Math.min(100, Math.max(0, dragState.current.startPosY - dy)),
                        })
                      }
                      const onEnd = () => {
                        dragState.current = null
                        window.removeEventListener('touchmove', onMove)
                        window.removeEventListener('touchend', onEnd)
                      }
                      window.addEventListener('touchmove', onMove, { passive: true })
                      window.addEventListener('touchend', onEnd)
                    } : undefined}
                  >
                    <div style={{ transform: `scale(${posterScale})`, transformOrigin: 'top left' }}>

                      {/* THE VISIBLE 1080x1080 PREVIEW (display only) */}
                      <div style={buildPosterStyle()}>

                        {/* Dark overlay on top of the background photo */}
                        <div style={{
                          position:        'absolute',
                          inset:           0,
                          backgroundColor: `rgba(0,0,0,${bgImageUrl ? overlayOpacity : 0})`,
                        }} />

                        {/* All poster content sits above the overlay */}
                        <div style={{
                          position:      'relative',
                          zIndex:        1,
                          width:         '100%',
                          height:        '100%',
                          padding:       '44px 60px 36px',
                          display:       'flex',
                          flexDirection: 'column',
                          boxSizing:     'border-box',
                          // overflow:hidden hard-clips anything that would
                          // escape the 1080px poster frame -- nothing can
                          // push the social footer out of the visible area
                          overflow:      'hidden',
                        }}>

                          {/* ── POSTER HEADER: performer name + date range ── */}
                          <div style={{
                            textAlign:     'center',
                            borderBottom:  `3px solid ${accentColor}`,
                            paddingBottom: '20px',
                            marginBottom:  '24px',
                          }}>
                            <p style={{
                              fontFamily:    hFont,
                              fontSize:      '56px',
                              color:         textColor,
                              textShadow:    `0 0 40px ${textColor}44`,
                              margin:        0,
                              lineHeight:    1,
                              letterSpacing: '.02em',
                            }}>
                              {performerName}
                            </p>
                            <p style={{
                              fontFamily: bFont,
                              fontSize:   '30px',
                              color:      textColor,
                              opacity:    .85,
                              margin:     '8px 0 0',
                            }}>
                              {monthLabel}
                            </p>
                          </div>


                          {/* ── TWO-COLUMN GIG LIST ── */}
                          {/* Both column divs always render - empty div
                              holds flex space so fill-right puts gigs on
                              the correct side instead of drifting left.
                              minHeight:0 + overflow:hidden lets this wrapper
                              shrink in a flex column so the social footer
                              is never pushed outside the poster boundary. */}
                          <div style={{ display: 'flex', gap: '40px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            {[leftCol, rightCol].map((col, colIdx) => (
                              <div key={colIdx} style={{
                                flex:           1,
                                minWidth:       0,
                                // minWidth:0 + overflow:hidden clips nowrap show
                                // names within the column instead of bleeding
                                // past it into the other column or the margin
                                overflow:       'hidden',
                                display:        'flex',
                                flexDirection:  'column',
                                // space-between based on THIS column's own row count.
                                // A remainder column with only 1-2 gigs stays packed
                                // at the top (flex-start) so there's no huge gap.
                                // The taller column spreads its rows evenly as normal.
                                justifyContent: col.length >= 3 ? 'space-between' : 'flex-start',
                              }}>
                                {col.map(gig => {

                                  // ── VENUE DETAIL LINE ────────────────────
                                  // venue | city, state | time | age | cover
                                  // Each segment only appears if it has a value.
                                  const segments = buildSegments(gig)

                                  return (
                                    <div
                                      key={gig.id}
                                      style={{
                                        padding:       rowPad(),
                                        borderBottom:  `1px solid ${accentColor}33`,
                                        display:       'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      {/* Line 1a: date -- body font, date color */}
                                      <span style={{ fontFamily: bFont, fontSize: gigFS(tallCol), color: dateColor, lineHeight: 1.25 }}>
                                        {fmtDate(gig.date)}
                                      </span>

                                      {/* Line 1b: show name -- scales down automatically when
                                          the name is long so it always fits without truncating */}
                                      <span style={{ fontFamily: hFont, fontSize: nameFS(tallCol, gig.name), color: textColor, lineHeight: 1.2, marginTop: '2px', display: 'block' }}>
                                        {gig.name || ''}
                                      </span>

                                      {/* Line 2: venue | city, state | time | age | cover */}
                                      {segments.length > 0 && (
                                        <span style={{ fontFamily: bFont, fontSize: venueFS(tallCol), color: accentColor, opacity: 1, marginTop: '3px', lineHeight: 1.25, display: 'block' }}>
                                          {segments.join(' | ')}
                                        </span>
                                      )}

                                    </div>
                                  )
                                })}
                              </div>
                            ))}
                          </div>


                          {/* ── POSTER FOOTER: social handles ── */}
                          {renderSocialFooter(bFont)}

                        </div>{/* end poster-content */}
                      </div>{/* end 1080x1080 poster */}

                    </div>
                  </div>{/* end scaled preview wrapper */}

                  {/* Drag hint -- only shown when a photo is loaded */}
                  {bgImageUrl && (
                    <p className="small text-muted mt-2 mb-0" style={{ maxWidth: `${previewSize}px`, textAlign: 'center' }}>
                      <i className="bi bi-arrows-move me-1"></i>
                      Drag the preview to reposition the photo
                    </p>
                  )}

                </div>
              )
            })

          )}

          {/* Download button sits below the poster preview(s) */}
          {filteredGigs.length > 0 && (
            <div className="mt-3">
              <button
                className="btn btn-primary w-100 fw-bold"
                disabled={exporting}
                onClick={handleDownload}
              >
                {exporting
                  ? <><i className="bi bi-hourglass-split me-2"></i>Generating...</>
                  : <><i className="bi bi-download me-2"></i>
                      Download {posterChunks.length > 1 ? `${posterChunks.length} Posters` : 'Poster'}
                    </>
                }
              </button>
              {exportMsg && (
                <p className="small text-center mt-2 fw-bold mb-0" style={{ color: 'var(--secondary-dark)' }}>
                  {exportMsg}
                </p>
              )}
            </div>
          )}

        </div>{/* end poster previews panel */}

      </div>{/* end responsive wrapper */}

    </div>
  )
}